/**
 * External inventory and variant API helpers
 * @module shared-cart/external/inventory
 */

import { SKU_BATCH_SIZE } from '../constants.js';

export interface Variant {
  id?: string;
  sku: string;
}

interface VariantLookupResponse {
  data?: {
    productVariants?: {
      edges?: Array<{ node: Variant }>;
    };
  };
}

export interface InventoryItem {
  stockKeepingUnit: string | number;
  isAvailable: boolean;
  isLowStock?: boolean;
}

export interface InventoryRequestItem {
  sku: string | number;
  id: string | number;
  quantity: number;
}

/**
 * @typedef {Object} Variant
 * @property {string} id - GraphQL GID (e.g., "gid://shopify/ProductVariant/123")
 * @property {string} sku - Product SKU
 */

/**
 * Fetches variant data by SKU from the products lookup endpoint
 * @param {string[]} skus - Array of SKUs to look up
 * @returns {Promise<Variant[]>} Array of variant objects
 */
export async function getVariantsBySku(skus: string[]): Promise<Variant[]> {
  const routeRoot = window.Shopify?.routes?.root || '/';
  const baseEndpoint = `${routeRoot}apps/products-lookup`;

  console.info(`[Shared Cart] Fetching variants for ${skus.length} SKUs`);

  try {
    // Split SKUs into chunks to stay within URL length limits
    const chunks: string[][] = [];
    for (let i = 0; i < skus.length; i += SKU_BATCH_SIZE) {
      chunks.push(skus.slice(i, i + SKU_BATCH_SIZE));
    }

    console.info(`[Shared Cart] Split into ${chunks.length} batch request(s)`);

    const results = await Promise.all(
      chunks.map(async (chunk, index): Promise<VariantLookupResponse> => {
        const params = new URLSearchParams({
          sourceSystem: 'Shopify',
          sourceOrigin: window.Shopify?.shop || '1akxhe-7p.myshopify.com',
          sku: chunk.join(','),
        });
        const url = `${baseEndpoint}?${params}`;

        console.info(`[Shared Cart] Batch ${index + 1}/${chunks.length}: GET ${url}`);

        try {
          const response = await fetch(url, { method: 'GET' });
          if (!response.ok) {
            console.error(`[Shared Cart] Batch ${index + 1} failed:`, response.status);
          }
          return response.json() as Promise<VariantLookupResponse>;
        } catch (error) {
          console.error(`[Shared Cart] Batch ${index + 1} error:`, error);
          return { data: { productVariants: { edges: [] } } };
        }
      }),
    );

    const variants = results.flatMap(
      (res) => res.data?.productVariants?.edges?.map((edge) => edge.node) || [],
    );

    console.info(`[Shared Cart] Retrieved ${variants.length} variants total`);
    return variants;
  } catch (error) {
    console.error('[Shared Cart] Error fetching variants by SKU:', error);
    throw error;
  }
}

/**
 * @typedef {Object} InventoryItem
 * @property {string} stockKeepingUnit - Product SKU
 * @property {boolean} isAvailable - Whether item is in stock
 * @property {boolean} isLowStock - Whether stock is low
 */

/**
 * Fetches inventory data for SKUs from the available-inventory endpoint
 * @param {string[]} skus - Array of SKUs to check inventory for
 * @returns {Promise<InventoryItem[]>} Array of inventory items
 */
export async function getInventories(skus: string[]): Promise<InventoryItem[]> {
  const routeRoot = window.Shopify?.routes?.root || '/';
  const baseEndpoint = `${routeRoot}apps/available-inventory`;

  // Get warehouse ID from localization settings
  const localizationObj = window?.Shopify?.localization_custom || '';
  if (!localizationObj) {
    console.warn('[Shared Cart] No localization object found');
    return [];
  }

  const currentCountryCode = localizationObj.country!.iso_code;
  const country = localizationObj.available_countries!.find((c) => c.code === currentCountryCode);
  const warehouseId = country?.shipping_profile_warehouse_id;

  if (!warehouseId) {
    console.warn('[Shared Cart] No warehouse ID found for country:', currentCountryCode);
    return [];
  }

  const params = new URLSearchParams({
    location: String(warehouseId),
    sku: skus.join(','),
  });
  const url = `${baseEndpoint}?${params}`;

  console.info(`[Shared Cart] Fetching inventory from warehouse ${warehouseId}:`, url);

  try {
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) {
      console.error(`[Shared Cart] Inventory request failed with status ${response.status}`);
      return [];
    }

    const inventories = (await response.json()) as InventoryItem[];
    console.info('[Shared Cart] Inventory data retrieved:', inventories);

    return inventories;
  } catch (error) {
    console.error('[Shared Cart] Error fetching inventory:', error);
    return [];
  }
}

/**
 * Checks if an item is available to order based on inventory data
 * @param {{sku: string|number, id: string|number, quantity: number}} item - The item to check
 * @param {InventoryItem[]} inventories - Available inventory data
 * @returns {boolean} True if the item is available
 */
export function checkItemAvailability(
  item: InventoryRequestItem,
  inventories: InventoryItem[],
): boolean {
  const inventoryItem = inventories.find(
    (inv) => inv.stockKeepingUnit.toString() === item.sku.toString(),
  );
  return inventoryItem?.isAvailable || false;
}
